import { Invader } from "./invader";
import { Bullet } from "./bullet";
import { CONFIG, GameConfig } from "./config";
import { gridSwarmSeeds } from "./layouts/grid";

export class Swarm {
  invaders: Invader[] = [];
  private direction = 1; // 1 = right, -1 = left
  private frame = 0;
  private animTimer = 0;
  private fireTimer = 0;
  private readonly cfg: GameConfig;
  private readonly fireInterval: number;
  private readonly total: number;

  constructor(level: number, cfg: GameConfig = CONFIG) {
    this.cfg = cfg;
    this.fireInterval = Math.max(
      cfg.swarm.minFireInterval,
      cfg.swarm.fireInterval - (level - 1) * cfg.swarm.fireIntervalStep
    );
    const seeds = (cfg.layout?.swarm ?? gridSwarmSeeds)(level, cfg);
    this.invaders = seeds.map((s) => new Invader(s.x, s.y, s.kind, cfg));
    this.total = this.invaders.length;
  }

  get aliveCount(): number {
    return this.invaders.filter((i) => i.alive).length;
  }

  get isCleared(): boolean {
    return this.aliveCount === 0;
  }

  private currentSpeed(aliveCount: number): number {
    const ratio = 1 - aliveCount / this.total;
    return (
      this.cfg.swarm.baseSpeed +
      (this.cfg.swarm.maxSpeed - this.cfg.swarm.baseSpeed) * ratio
    );
  }

  /** Advances the fleet. Returns true if it has reached the invasion line. */
  update(dt: number, invasionY: number): boolean {
    const alive = this.invaders.filter((i) => i.alive);
    if (alive.length === 0) return false;

    this.animTimer += dt;
    if (this.animTimer >= this.cfg.swarm.animInterval) {
      this.animTimer = 0;
      this.frame = this.frame === 0 ? 1 : 0;
    }

    // A plain loop rather than Math.min(...alive.map(...)): the hero fleet is
    // ~145 invaders, and spreading that array every frame is both wasteful and
    // a latent argument-count hazard.
    let minX = Infinity;
    let maxX = -Infinity;
    for (const inv of alive) {
      if (inv.x < minX) minX = inv.x;
      if (inv.x + inv.width > maxX) maxX = inv.x + inv.width;
    }

    const dx = this.direction * this.currentSpeed(alive.length) * dt;
    const hitEdge = maxX + dx > this.cfg.canvas.width || minX + dx < 0;

    if (hitEdge) {
      // Reverse and step inward (by -dx) on the same frame so the fleet
      // never stalls horizontally or crosses the wall.
      this.direction *= -1;
      for (const inv of alive) {
        inv.y += this.cfg.swarm.stepDown;
        inv.x -= dx;
      }
    } else {
      for (const inv of alive) inv.x += dx;
    }

    return alive.some((i) => i.y + i.height >= invasionY);
  }

  /** Returns a new invader bullet when the fire timer elapses, else null. */
  tryFire(dt: number): Bullet | null {
    this.fireTimer += dt;
    if (this.fireTimer < this.fireInterval) return null;
    this.fireTimer = 0;
    const shooter = this.pickShooter();
    if (!shooter) return null;
    return new Bullet(
      shooter.x + shooter.width / 2 - this.cfg.bullet.width / 2,
      shooter.y + shooter.height,
      "invader",
      this.cfg
    );
  }

  /** Bottom-most invader in a random occupied column. */
  private pickShooter(): Invader | null {
    const columns = new Map<number, Invader>();
    for (const inv of this.invaders) {
      if (!inv.alive) continue;
      const key = Math.round(inv.x);
      const existing = columns.get(key);
      if (!existing || inv.y > existing.y) columns.set(key, inv);
    }
    const shooters = Array.from(columns.values());
    if (shooters.length === 0) return null;
    const random = this.cfg.random ?? Math.random;
    return shooters[Math.floor(random() * shooters.length)];
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const inv of this.invaders) {
      if (inv.alive) inv.draw(ctx, this.frame);
    }
  }
}
